import {
  Alert,
  Button,
  Checkbox,
  Collapse,
  DatePicker,
  Divider,
  Input,
  Radio,
  Select,
  Tag,
  Typography
} from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import d from 'dayjs';
import { toast } from 'react-hot-toast';
import api from 'src/api';
import BookingDetailsCard from './booking-details-card';
import {
  ALL_WEEKDAYS,
  WEEKDAY_OPTIONS,
  formatRangeLabel,
  toKey
} from 'src/utils/calendar-range';

/**
 * Side panel (Booking.com style): shows the selected range, lets the user
 * adjust start / end dates and weekday filter, and applies availability,
 * rooms to sell, price and reason to every selected date in one request.
 */
function CalendarBulkPanel({
  propertyId,
  property,
  room,
  roomId,
  range,
  onRangeChange,
  daysOfWeek,
  onDaysOfWeekChange,
  targetDates,
  entriesByKey,
  isFetching,
  setUpdatingCalendar,
  isMobile
}) {
  const queryClient = useQueryClient();
  const isHotel = property?.propertyType === 'hotel';
  const isSingle = !!range?.start && range.start === (range.end || range.start);
  const startEntry = range?.start ? entriesByKey[range.start] : null;
  const roomCount = room?.count || 0;

  const bookedTargets = useMemo(
    () => targetDates.filter(key => entriesByKey[key]?.bookingId),
    [targetDates, entriesByKey]
  );
  const editableCount = targetDates.length - bookedTargets.length;

  const [availability, setAvailability] = useState(null); // 'open' | 'closed' | null (unchanged)
  const [toSell, setToSell] = useState(roomCount);
  const [price, setPrice] = useState('');
  const [reason, setReason] = useState('');

  // Prefill from the selected date when a single date is selected; for a range
  // start blank so only the fields the user fills in are applied.
  useEffect(() => {
    if (isSingle) {
      const blockedCount = startEntry?.roomCount || 0;
      const blocked =
        !!startEntry?.isBlocked ||
        (isHotel && !!startEntry && roomCount > 0 && blockedCount >= roomCount);
      setAvailability(blocked ? 'closed' : 'open');
      setToSell(isHotel ? Math.max(roomCount - blockedCount, 0) : 0);
      setPrice(startEntry?.price ?? '');
      setReason(startEntry?.reason || '');
    } else {
      setAvailability(null);
      setToSell(roomCount);
      setPrice('');
      setReason('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    range?.start,
    range?.end,
    startEntry?._id,
    startEntry?.updatedAt,
    roomCount,
    isHotel
  ]);

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      const payload = {
        propertyId,
        from: range.start,
        to: range.end || range.start,
        daysOfWeek
      };
      if (isHotel) payload.roomTypeId = roomId;
      if (availability === 'closed') {
        payload.isBlocked = true;
        if (isHotel) payload.roomCount = roomCount;
        payload.reason = reason || '';
      } else if (availability === 'open') {
        payload.isBlocked = false;
        if (isHotel) payload.roomCount = Math.max(roomCount - toSell, 0);
        payload.reason = '';
      }
      if (price !== '' && price !== null) payload.price = Number(price);

      const { data } = await api.post('/calendar/bulk', payload);
      return data;
    },
    onMutate: () => setUpdatingCalendar(true),
    onSuccess: data => {
      const updated = data?.updated ?? 0;
      const skipped = data?.skipped ?? 0;
      toast.success(
        `${updated} date${updated === 1 ? '' : 's'} updated` +
          (skipped ? ` · ${skipped} booked date${skipped === 1 ? '' : 's'} skipped` : '')
      );
      queryClient.invalidateQueries({ queryKey: ['calendar-entries', propertyId] });
    },
    onError: err => {
      toast.error(err?.response?.data?.message || 'Something went wrong');
    },
    onSettled: () => setUpdatingCalendar(false)
  });

  const handleSave = () => {
    if (!range?.start) return toast.error('Select a date first');
    if (!daysOfWeek.length) return toast.error('Select at least one day of the week');
    if (!targetDates.length) return toast.error('No dates match your selection');
    if (!editableCount) return toast.error('All selected dates are booked');
    if (availability === null && (price === '' || price === null))
      return toast.error('Choose Open / Closed or enter a price');
    if (price !== '' && price !== null && (Number.isNaN(Number(price)) || Number(price) < 0))
      return toast.error('Enter a valid price');
    mutate();
  };

  const currency =
    room?.currency?.currencyId ||
    property?.apartment?.prices?.[0]?.currency?.currencyId ||
    'MAD';
  const controlSize = isMobile ? 'middle' : 'large';
  const today = d().startOf('day');
  const weekdayLabel =
    daysOfWeek.length === ALL_WEEKDAYS.length
      ? ''
      : ` (only ${WEEKDAY_OPTIONS.filter(o => daysOfWeek.includes(o.value))
          .map(o => o.label)
          .join(', ')})`;

  const labelStyle = {
    fontSize: isMobile ? '13px' : '14px',
    fontWeight: 500,
    display: 'block',
    marginBottom: '4px'
  };

  return (
    <div
      style={{
        width: '100%',
        border: '1px solid #8d9197',
        background: '#fff',
        borderRadius: '10px',
        padding: isMobile ? '12px' : '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile ? '8px' : '10px'
      }}
    >
      <div>
        <Typography.Title level={5} style={{ margin: 0 }}>
          {targetDates.length} {targetDates.length === 1 ? 'date' : 'dates'} selected
        </Typography.Title>
        <Typography.Text type="secondary">{formatRangeLabel(range)}</Typography.Text>
      </div>

      <div>
        <label style={labelStyle}>Start date</label>
        <DatePicker
          size={controlSize}
          style={{ width: '100%' }}
          value={range?.start ? d(range.start) : null}
          allowClear={false}
          format="D MMM YYYY"
          disabledDate={cur => cur && cur.startOf('day').isBefore(today)}
          onChange={val => {
            if (!val) return;
            const start = toKey(val);
            const end =
              range?.end && !d(range.end).isBefore(val, 'day') ? range.end : start;
            onRangeChange({ start, end }, { navigate: true });
          }}
        />
      </div>
      <div>
        <label style={labelStyle}>End date</label>
        <DatePicker
          size={controlSize}
          style={{ width: '100%' }}
          value={range?.end ? d(range.end) : range?.start ? d(range.start) : null}
          allowClear={false}
          format="D MMM YYYY"
          disabledDate={cur =>
            cur &&
            (cur.startOf('day').isBefore(today) ||
              (range?.start && cur.isBefore(d(range.start), 'day')))
          }
          onChange={val => {
            if (!val) return;
            onRangeChange(
              { start: range?.start || toKey(val), end: toKey(val) },
              { navigate: false }
            );
          }}
        />
      </div>

      <Collapse
        ghost
        size="small"
        defaultActiveKey={['advanced']}
        style={{ margin: '0 -12px' }}
        items={[
          {
            key: 'advanced',
            label: 'Advanced date selection',
            children: (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <Typography.Text>
                  Which days of the week do you want to apply changes to?
                </Typography.Text>
                <Checkbox.Group
                  options={WEEKDAY_OPTIONS}
                  value={daysOfWeek}
                  onChange={onDaysOfWeekChange}
                />
                {daysOfWeek.length !== ALL_WEEKDAYS.length && (
                  <Button
                    type="link"
                    size="small"
                    style={{ alignSelf: 'flex-start', padding: 0 }}
                    onClick={() => onDaysOfWeekChange(ALL_WEEKDAYS)}
                  >
                    Select all days
                  </Button>
                )}
              </div>
            )
          }
        ]}
      />

      <Divider style={{ margin: '4px 0' }} />

      {isSingle && startEntry?.bookingId ? (
        <>
          <Tag color="green" style={{ width: 'fit-content' }}>
            Booked
          </Tag>
          <BookingDetailsCard entry={startEntry} />
        </>
      ) : (
        <>
          {bookedTargets.length > 0 && (
            <Alert
              type="info"
              showIcon
              message={`${bookedTargets.length} booked ${
                bookedTargets.length === 1 ? 'date' : 'dates'
              } in this selection will be skipped.`}
            />
          )}

          <div>
            <label style={labelStyle}>Open or close for bookings</label>
            <Radio.Group
              value={availability}
              onChange={e => setAvailability(e.target.value)}
              disabled={isPending || isFetching}
            >
              <Radio value="open">Open</Radio>
              <Radio value="closed">Closed</Radio>
            </Radio.Group>
            {!isSingle && availability !== null && (
              <Button
                type="link"
                size="small"
                style={{ padding: 0, marginLeft: '8px' }}
                onClick={() => setAvailability(null)}
              >
                Leave unchanged
              </Button>
            )}
          </div>

          {isHotel && (
            <div>
              <label style={labelStyle}>Rooms to sell</label>
              <Select
                size={controlSize}
                style={{ width: '100%' }}
                value={toSell}
                disabled={availability !== 'open'}
                onChange={setToSell}
                options={Array.from({ length: roomCount + 1 }).map((_, i) => ({
                  value: i,
                  label: `${i} of ${roomCount} rooms to sell`
                }))}
              />
            </div>
          )}

          <div>
            <label style={labelStyle}>Price ({currency})</label>
            <Input
              size={controlSize}
              type="number"
              min={0}
              placeholder={isSingle ? 'Enter price' : 'Leave empty to keep existing prices'}
              value={price}
              onChange={e => setPrice(e.target.value)}
            />
          </div>

          <div>
            <label style={labelStyle}>Reason (optional)</label>
            <Input
              size={controlSize}
              placeholder="Why are these dates closed?"
              disabled={availability !== 'closed'}
              value={reason}
              onChange={e => setReason(e.target.value)}
            />
          </div>

          <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
            Changes will be made to {editableCount}{' '}
            {editableCount === 1 ? 'date' : 'dates'}: {formatRangeLabel(range, 'D MMMM YYYY')}
            {weekdayLabel}.
          </Typography.Text>

          <Button
            type="primary"
            size={controlSize}
            block
            onClick={handleSave}
            loading={isPending}
            disabled={isFetching}
          >
            Save
          </Button>
        </>
      )}
    </div>
  );
}

export default CalendarBulkPanel;
